import { Heading } from 'components/ui/Heading/Heading';
import { ExclamationCircle } from 'icons/ExclamationCircle';
import { useSelector } from 'react-redux';
import { getOrder } from 'selectors/entities/order/order';
import { useTranslations } from 'utils/translations/useTranslations';

const PausedError = () => {
  const i18n = useTranslations();
  const { id: orderId } = useSelector(getOrder) || {};
  const supportCode = orderId ? String(orderId).slice(-8) : '';

  return (
    <div className="flex flex-col items-center text-center space-y-6">
      <div className="flex items-center justify-center">
        <svg
          className="w-12 h-12 text-black"
          fill="none"
          viewBox="0 0 54 54"
          aria-hidden="true"
        >
          <g clipPath="url(#clip0_26112_759)">
            <path
              d="M27 51.6875C12.257 47.7545 2.15625 39.0117 2.15625 23.5312V2H51.8438V23.5312C51.8438 39.0006 41.7578 47.75 27 51.6875Z"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M35.3887 21.8105H18.6113C16.7581 21.8105 15.2559 23.3128 15.2559 25.166V35.2324C15.2559 37.0856 16.7581 38.5879 18.6113 38.5879H35.3887C37.2419 38.5879 38.7441 37.0856 38.7441 35.2324V25.166C38.7441 23.3128 37.2419 21.8105 35.3887 21.8105Z"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M18.6113 21.8105V16.7773C18.6113 14.5525 19.4951 12.4188 21.0682 10.8457C22.6414 9.27248 24.7751 8.38867 27 8.38867C29.2249 8.38867 31.3586 9.27248 32.9318 10.8457C34.5049 12.4188 35.3887 14.5525 35.3887 16.7773V21.8105"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <defs>
            <clipPath id="clip0_26112_759">
              <rect width="53.6875" height="53.6875" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </div>

      <Heading
        id="paused-error-title"
        as="h1"
        size="large"
        className="text-gray-900 font-bold max-w-lg"
      >
        {i18n.t('pausedError.title')}
      </Heading>

      <div className="w-full bg-amber-50 border border-amber-200 rounded-lg p-5">
        <div className="flex items-start space-x-3">
          <div className="text-left">
            <p className="font-semibold text-gray-800 mb-3">
              <ExclamationCircle
                aria-hidden={true}
                className="w-4 h-4 mr-2 text-amber-600"
              />
              {i18n.t('pausedError.subtitle')}
            </p>
            <p className="text-gray-700 leading-relaxed text-sm">
              {i18n.t('pausedError.error')}
            </p>
          </div>
        </div>
      </div>

      <div className="w-full border border-gray-200 rounded-lg p-6 bg-white">
        <div className="flex flex-col items-center space-y-4">
          <div className="flex items-center justify-center space-x-3">
            <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            </div>

            <Heading
              as="h2"
              size="medium"
              className="font-semibold text-gray-800 flex items-center mb-0"
            >
              {i18n.t('pausedError.supportTeam')}
            </Heading>
          </div>

          <p className="text-gray-600 text-center">
            {i18n.t('pausedError.supportTeamDescription')}
          </p>

          <a
            href="https://help.flywire.com/hc/en-us/p/call-us"
            className="text-blue-600 underline hover:text-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-300 focus:ring-offset-1 rounded"
            target="_blank"
            rel="noopener noreferrer"
          >
            {i18n.t('pausedError.supportNumbers')}
          </a>

          <div className="w-full bg-gray-200 rounded-lg p-6 text-center">
            <p className="text-gray-600 mb-2 text-sm">
              {i18n.t('pausedError.supportCode')}
            </p>
            <p className="text-2xl font-bold text-gray-900 tracking-wider mb-0">
              {supportCode}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export { PausedError };
